import { useRef, useState } from "react";
import {
  clearLinkedProject,
  parseLinkedProjectJson,
  writeLinkedProject,
  type LinkedProject,
} from "./registry.ts";

/**
 * Dialog för att koppla den öppna centralen till ett projekt i schemaeditorn.
 * Användaren klistrar in eller laddar upp en JSON-fil som tidigare exporterats
 * från schemaeditorn (toolbar → "Centralspec"). Vid lyckad validering sparas
 * paketet i localStorage så att "Användning"-autokomplett kan förslå etiketter.
 *
 * Kopplingen är per centralspec-dokument — en panels-doc kan ha en editor-
 * projekt-koppling i taget.
 */
export function LinkProjectDialog({
  documentId,
  existing,
  onClose,
  onLinked,
}: {
  documentId: string;
  existing: LinkedProject | null;
  onClose: () => void;
  onLinked: (project: LinkedProject | null) => void;
}) {
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const tryParse = (raw: string) => {
    const parsed = parseLinkedProjectJson(raw);
    if (!parsed) {
      setError(
        "Kunde inte läsa filen. Kontrollera att du valde en JSON-fil exporterad från schemaeditorn (knapp 'Centralspec' i toolbaren).",
      );
      return;
    }
    writeLinkedProject(documentId, parsed);
    onLinked(parsed);
    onClose();
  };

  const onFile = async (file: File) => {
    try {
      const raw = await file.text();
      tryParse(raw);
    } catch {
      setError("Kunde inte läsa filen — försök igen.");
    }
  };

  const onUnlink = () => {
    clearLinkedProject(documentId);
    onLinked(null);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50"
      role="dialog"
      aria-label="Koppla till schemaeditor-projekt"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl rounded-lg bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold">Koppla till projekt</h2>
            <p className="text-sm text-slate-500">
              Importera apparater från ett schemaeditor-projekt så att du kan
              använda deras beteckningar och etiketter som förslag i
              "Användning"-kolumnen.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded px-2 py-1 text-slate-500 hover:bg-slate-100"
            aria-label="Stäng"
          >
            ×
          </button>
        </div>

        {existing ? (
          <div className="mb-3 rounded border border-emerald-200 bg-emerald-50 p-3 text-sm">
            <div className="font-medium text-emerald-900">
              Kopplad till: {existing.projectTitle || existing.documentId || "okänt projekt"}
            </div>
            <div className="text-emerald-700">
              {existing.devices.length} apparat{existing.devices.length === 1 ? "" : "er"} ·
              importerad {new Date(existing.importedAt).toLocaleString("sv-SE")}
            </div>
            <button
              type="button"
              onClick={onUnlink}
              className="mt-2 text-xs text-rose-700 hover:underline"
            >
              Koppla bort
            </button>
          </div>
        ) : null}

        <div className="mb-3">
          <div className="mb-1 text-sm font-medium">Läs in från fil</div>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onFile(f);
            }}
            className="text-sm"
          />
          <p className="mt-1 text-xs text-slate-500">
            Använd "Centralspec"-knappen i schemaeditorns toolbar för att
            ladda ner en JSON-fil med ditt projekts apparater.
          </p>
        </div>

        <div className="mb-3">
          <div className="mb-1 text-sm font-medium">…eller klistra in JSON</div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={6}
            placeholder='{"version": 1, "source": "electro-planner-editor", ...}'
            className="w-full rounded border border-slate-300 p-2 font-mono text-xs"
          />
        </div>

        {error ? (
          <div className="mb-3 rounded border border-rose-200 bg-rose-50 p-2 text-sm text-rose-800">
            {error}
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-slate-300 bg-white px-3 py-1 text-sm hover:bg-slate-50"
          >
            Avbryt
          </button>
          <button
            type="button"
            onClick={() => tryParse(text)}
            disabled={text.trim().length === 0}
            className="rounded bg-sky-600 px-3 py-1 text-sm text-white hover:bg-sky-700 disabled:bg-slate-300"
          >
            Importera
          </button>
        </div>
      </div>
    </div>
  );
}
